// components/Footer/Footer.jsx
import { Box, Container, Grid, Typography, Link, Divider, Stack, Chip, alpha, IconButton, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { keyframes } from '@mui/system';
import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
  Public,
  Diamond,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  Verified,
  Security,
  WhatsApp,
} from '@mui/icons-material';

const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const Footer = () => {
  const locations = [
    {
      flag: '🇨🇦',
      country: 'CANADA',
      address: '158 Barkley Terrace\nVictoria, British Columbia, Canada, V8S-2J6',
      phone: '+1-250-883-5000',
      whatsapp: '12508835000', // WhatsApp number without '+' or spaces
      email: 'canada@pentillion.com',
      hours: 'Mon-Fri: 9am - 6pm PST',
      timezone: 'PST',
    },
    {
      flag: '🇳🇿',
      country: 'NEW ZEALAND',
      address: '14, 19 Lantana Avenue\nAuckland, New Zealand, 0630',
      phone: '+64 21 1983352',
      whatsapp: '64211983352', // WhatsApp number without '+' or spaces
      email: 'nz@pentillion.com',
      hours: 'Mon-Fri: 9am - 6pm NZST',
      timezone: 'NZST',
    },
    {
      flag: '🇿🇦',
      country: 'SOUTH AFRICA',
      address: 'To be confirmed\nJohannesburg, South Africa',
      phone: '+27 123 456 789',
      whatsapp: '27123456789', // WhatsApp number without '+' or spaces
      email: 'sa@pentillion.com',
      hours: 'Mon-Fri: 9am - 6pm SAST',
      timezone: 'SAST',
      isTbc: true,
    },
  ];

  const socialLinks = [
    { icon: <Facebook />, url: 'https://facebook.com', label: 'Facebook', color: '#1877F2' },
    { icon: <Twitter />, url: 'https://twitter.com', label: 'Twitter', color: '#1DA1F2' },
    { icon: <LinkedIn />, url: 'https://linkedin.com', label: 'LinkedIn', color: '#0A66C2' },
    { icon: <Instagram />, url: 'https://instagram.com', label: 'Instagram', color: '#E4405F' },
    { icon: <WhatsApp />, url: `https://wa.me/12508835000`, label: 'WhatsApp', color: '#25D366' },
  ];

  return (
    <Box 
      component="footer" 
      sx={{ 
        bgcolor: '#000066',
        mt: 'auto', 
        borderTop: '1px solid rgba(212, 175, 55, 0.2)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
          backgroundSize: '200% 100%',
          animation: `${goldShimmer} 3s linear infinite`,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Main Footer Content */}
        <Grid container spacing={5}>
          {/* Brand Column - Using Actual Logo */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box 
                component="img"
                src="/images/pentillion_logo.png"
                alt="Pentillion Logo"
                sx={{
                  height: 55,
                  width: 'auto',
                  transition: 'transform 0.3s ease',
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.6), mb: 2.5, lineHeight: 1.7 }}>
              Complete Precious Metals Ecosystem — Mining, Refining, Processing & Distribution with global excellence since 1995.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              <Chip 
                label="LBMA Accredited" 
                size="small"
                icon={<Verified sx={{ fontSize: 14 }} />}
                sx={{ 
                  bgcolor: alpha('#D4AF37', 0.12), 
                  color: '#D4AF37',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: '#D4AF37',
                  },
                }}
              />
              <Chip 
                label="ISO Certified" 
                size="small"
                icon={<Security sx={{ fontSize: 14 }} />}
                sx={{ 
                  bgcolor: alpha('#D4AF37', 0.12), 
                  color: '#D4AF37',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: '#D4AF37',
                  },
                }}
              />
            </Stack>
            
            {/* Social Links - Premium */}
            <Stack direction="row" spacing={1.5}>
              {socialLinks.map((social, index) => (
                <Tooltip title={social.label} key={index}>
                  <IconButton
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: alpha('#FFFFFF', 0.5),
                      bgcolor: alpha('#FFFFFF', 0.03),
                      border: '1px solid rgba(212, 175, 55, 0.15)',
                      '&:hover': {
                        color: social.color,
                        bgcolor: alpha(social.color, 0.1),
                        borderColor: alpha(social.color, 0.3),
                        transform: 'translateY(-3px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {social.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Grid>

          {/* Quick Links - Operations */}
          <Grid item xs={6} md={2}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 3, 
                color: '#D4AF37',
                fontWeight: 700,
                letterSpacing: 1.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, transparent)',
                  borderRadius: 1,
                },
              }}
            >
              Operations
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {['Mining', 'Refining', 'Metal Processing', 'Distribution'].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1.5 }}>
                  <Link 
                    component={RouterLink} 
                    to={`/${item.toLowerCase().replace(' ', '-')}`} 
                    sx={{ 
                      color: alpha('#FFFFFF', 0.6), 
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        color: '#D4AF37',
                        transform: 'translateX(6px)',
                      },
                    }}
                  >
                    <span style={{ fontSize: '0.5rem', color: '#D4AF37' }}>◆</span>
                    {item}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Quick Links - Resources */}
          <Grid item xs={6} md={2}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 3, 
                color: '#D4AF37',
                fontWeight: 700,
                letterSpacing: 1.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, transparent)',
                  borderRadius: 1,
                },
              }}
            >
              Resources
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {['Market Data', 'Resources', 'Contact'].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1.5 }}>
                  <Link 
                    component={RouterLink} 
                    to={`/${item.toLowerCase().replace(' ', '-')}`} 
                    sx={{ 
                      color: alpha('#FFFFFF', 0.6), 
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      transition: 'all 0.3s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        color: '#D4AF37',
                        transform: 'translateX(6px)',
                      },
                    }}
                  >
                    <span style={{ fontSize: '0.5rem', color: '#D4AF37' }}>◆</span>
                    {item}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Global Locations - Premium */}
          <Grid item xs={12} md={5}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 3, 
                color: '#D4AF37',
                fontWeight: 700,
                letterSpacing: 1.5,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
                display: 'inline-flex',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  background: 'linear-gradient(90deg, #D4AF37, transparent)',
                  borderRadius: 1,
                },
              }}
            >
              <Public sx={{ fontSize: 16 }} />
              Global Offices
            </Typography>
            <Grid container spacing={2}>
              {locations.map((loc, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Box 
                    sx={{ 
                      p: 2,
                      bgcolor: alpha('#FFFFFF', 0.02),
                      borderRadius: 2,
                      border: '1px solid rgba(212, 175, 55, 0.12)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      height: '100%',
                      '&:hover': {
                        borderColor: alpha('#D4AF37', 0.4),
                        transform: 'translateY(-5px)',
                        bgcolor: alpha('#D4AF37', 0.04),
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: '2rem', mb: 0.5 }}>
                      {loc.flag}
                    </Typography>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: '#D4AF37', 
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: 1,
                        mb: 1,
                      }}
                    >
                      {loc.country}
                    </Typography>
                    <Box sx={{ mb: 1.5 }}>
                      {loc.address.split('\n').map((line, i) => (
                        <Typography 
                          key={i} 
                          variant="caption" 
                          sx={{ 
                            color: alpha('#FFFFFF', 0.5), 
                            display: 'block',
                            fontSize: '0.65rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                    <Stack spacing={0.75} sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: 11, color: alpha('#D4AF37', 0.6) }} />
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.65rem' }}>
                          {loc.phone}
                        </Typography>
                      </Box>
                      {/* WhatsApp Icon - No number displayed */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`WhatsApp ${loc.country}`}>
                          <IconButton
                            component="a"
                            href={`https://wa.me/${loc.whatsapp}?text=${encodeURIComponent('Hello! I have a question about Pentillion services.')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ 
                              p: 0,
                              color: '#25D366',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                              transition: 'transform 0.2s ease',
                            }}
                          >
                            <WhatsApp sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.65rem' }}>
                          WhatsApp
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ fontSize: 11, color: alpha('#D4AF37', 0.6) }} />
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.65rem' }}>
                          {loc.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ fontSize: 11, color: alpha('#D4AF37', 0.6) }} />
                        <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.65rem' }}>
                          {loc.hours}
                        </Typography>
                      </Box>
                    </Stack>
                    {loc.isTbc && (
                      <Chip 
                        label="Opening Soon" 
                        size="small"
                        sx={{ 
                          bgcolor: alpha('#D4AF37', 0.15), 
                          color: '#D4AF37',
                          fontSize: '0.55rem',
                          height: 20,
                          mt: 1,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(212, 175, 55, 0.1)' }} />

        {/* Bottom Bar - Premium */}
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.4), fontSize: '0.7rem', textAlign: { xs: 'center', md: 'left' } }}>
              © {new Date().getFullYear()} Pentillion Precious Metals. All rights reserved.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack 
              direction="row" 
              spacing={3} 
              justifyContent={{ xs: 'center', md: 'flex-end' }}
              sx={{ 
                flexWrap: 'wrap',
                gap: 2,
                '& a': {
                  color: alpha('#FFFFFF', 0.4),
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#D4AF37',
                  },
                },
              }}
            >
              <Link component={RouterLink} to="/privacy">Privacy Policy</Link>
              <Link component={RouterLink} to="/terms">Terms of Service</Link>
              <Link component={RouterLink} to="/compliance">Compliance</Link>
              <Link component={RouterLink} to="/sitemap">Sitemap</Link>
            </Stack>
          </Grid>
        </Grid>

        {/* Disclaimer - Premium */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(212, 175, 55, 0.08)' }}>
          <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.3), display: 'block', textAlign: 'center', fontSize: '0.6rem' }}>
            Pentillion is a globally integrated precious metals group. All transactions comply with international trade laws and LBMA guidelines.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;